import { User, UserId } from '../../user';
import { tuple } from '../lang/tuple';

export interface HasId<TId = number> {
    id: TId;
}

export interface HasTimestamps {
    createdAt: Date;
    updatedAt: Date | null;
}

export interface Auditable extends HasTimestamps {
    createdById?: UserId | null;
    createdBy?: User;
    updatedById?: UserId | null;
    updatedBy?: User;
}

export interface ParanoidTimestamp {
    deletedAt?: Date | null;
}

export interface ParanoidAuditable extends ParanoidTimestamp {
    deletedById?: UserId | null;
    deletedBy?: User;
}

export interface FullAuditable extends Auditable, ParanoidAuditable {}

export function fullAuditable<TModel>(model: TModel): FullAuditable & TModel {
    return model as FullAuditable & TModel;
}

/**
 * @deprecated
 * Creating objects to persist in the DB requires an ID, but we actually want it to be "undefined" so that it gets
 * generated. So, we subvert the type system, so consuming code can conform to the required interface.
 * @param model - The model
 * @returns - The model with the ID
 */
export function withId<TModel>(model: TModel): HasId<any> & TModel {
    return model as HasId<any> & TModel;
}

/**
 * @deprecated
 * Creating objects to persist in the DB requires an ID, but we actually want it to be "undefined" so that it gets
 * generated. So, we subvert the type system, so consuming code can conform to the required interface.
 * @param model - The model
 * @returns - The model with the ID and GraphQL ID
 */
export function withIdAndGraphqlId<TModel>(model: TModel): HasId<any> & TModel & { graphqlId: string } {
    return model as HasId<any> & TModel & { graphqlId: string };
}

export function withIdAndTimestamps<TModel>(model: TModel): HasId<any> & HasTimestamps & TModel {
    return model as HasId<any> & HasTimestamps & TModel;
}

export type IdAndTimestampsKeys = 'createdAt' | 'id' | 'updatedAt';

export type AuditableKeys = 'createdAt' | 'createdById' | 'updatedAt' | 'updatedById';

export type IdAndAuditableKeys = AuditableKeys | 'id';

export const fullAuditableKeys = tuple(
    'createdAt',
    'createdById',
    'updatedAt',
    'updatedById',
    'deletedAt',
    'deletedById',
);
export type FullAuditableKeys = typeof fullAuditableKeys[number];

export const idAndFullAuditableKeys = tuple(
    'id',
    'createdAt',
    'createdById',
    'updatedAt',
    'updatedById',
    'deletedAt',
    'deletedById',
);
export type IdAndFullAuditableKeys = typeof idAndFullAuditableKeys[number];
